/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import { ICompanyForm, IDiscount, IStore, IUser } from '../FormPartner';
import { useTheme } from '../../../contexts/ThemeProvider';
import { Input } from '../../../components/atoms/Input';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { Button } from '../../../components/atoms/Button';
import { Text } from '../../../components/atoms/Text';
import { sendPartnerEnquiry } from '@partiu/api/club/api';
interface IForm {
  email: string;
  password: string;
}

export const SetPasswordModal = ({
  isOpen,
  onClose,
  company,
  store,
  discount,
  user,
  exclusive,
  onAccept,
}: {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  company: ICompanyForm;
  store: IStore;
  discount: IDiscount;
  user: IUser;
  exclusive: boolean;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [worked, setWorked] = useState(false);
  const [link, setLink] = useState('');

  const { id, colors } = useTheme();
  const toast = useToast();

  const passwordSchema = z
    .string()
    .min(8, { message: 'A senha deve ter pelo menos 8 caracteres.' })
    .regex(/[A-Z]/, { message: 'A senha deve conter pelo menos uma letra maiúscula.' })
    .regex(/[a-z]/, { message: 'A senha deve conter pelo menos uma letra minúscula.' })
    .regex(/[0-9]/, { message: 'A senha deve conter pelo menos um número.' })
    .regex(/[^a-zA-Z0-9]/, { message: 'A senha deve conter pelo menos um caractere especial.' });

  const formSchema = z.object({
    email: z.string().email(),
    password: passwordSchema,
  });

  const methods = useForm<IForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: user?.email || '',
      password: '',
    },
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = methods;

  async function onSubmit(payloadForm: IForm) {
    if (worked) {
      onClose();
      onAccept();
      return;
    }

    try {
      setIsLoading(true);

      const companyData = {
        cellphone: company?.cellphone?.replace(/\D/g, ''),
        cnpj: company?.cnpj?.replace(/\D/g, ''),
        corporate_name: company?.corporate_name,
        name: company?.name,
        owner: user?.full_name,
        email: user?.email,
        description: company?.description,
        category: company?.category,
        approved_data: true,
        creator_origin: 'landing',
        site: company?.site,
        creator_agreement: id,
        agreement: exclusive ? id : undefined,
      };

      const userData = {
        full_name: user?.full_name,
        email: user?.email,
        password: payloadForm?.password,
      };

      const storeData = {
        postal_code: store?.postal_code?.replace(/\D/g, '') || '',
        street: store?.street || '',
        number: store?.number ? Number(store?.number) : 0,
        neighborhood: store?.neighborhood || '',
        city: store?.city || '',
        state: store?.state || '',
        country: store?.country || '',
      };

      const discountData: any = {
        rules: discount?.rulesText,
        type: discount?.type,
        discount: discount?.discount ? Number(discount?.discount) : 0,
        name: discount?.title_discount,
        mode: discount?.mode,
      };

      if (discount?.mode === 'online') {
        discountData.link = discount?.link_desconto || '';
        discountData.coupon = discount?.cupom_desconto || '';
      }

      const data = new FormData();

      const whiteSpace =
        '\u0009\u000a\u000b\u000c\u000d\u0020\u00a0' +
        '\u1680\u180e\u2000\u2001\u2002\u2003\u2004' +
        '\u2005\u2006\u2007\u2008\u2009\u200a\u200b' +
        '\u2028\u2029\u202f\u205f\u3000';

      const re = new RegExp(`[${whiteSpace}]`, 'gmu');

      data.append(
        'company',
        `${JSON.stringify(JSON.stringify(companyData, null, '\t'))
          .replace(re, '\u0020')
          .replace(/ +/, '\u0020')}`
      );

      if (storeData) {
        data.append(
          'store',
          `${JSON.stringify(JSON.stringify(storeData, null, '\t'))
            .replace(re, '\u0020')
            .replace(/ +/, '\u0020')}`
        );
      }

      data.append(
        'discount',
        `${JSON.stringify(JSON.stringify(discountData, null, '\t'))
          .replace(re, '\u0020')
          .replace(/ +/, '\u0020')}`
      );
      data.append(
        'user',
        `${JSON.stringify(JSON.stringify(userData, null, '\t'))
          .replace(re, '\u0020')
          .replace(/ +/, '\u0020')}`
      );

      if (company.cover_photo) {
        data.append('cover_photo', company.cover_photo);
      }

      if (company.logo) {
        data.append('logo', company.logo);
      }

      if (discount.image) {
        data.append('discount_image', discount?.image);
      }

      const response = await sendPartnerEnquiry(data);

      if (response.status === 201) {
        if (response?.data?.link) {
          setWorked(true);
          setLink(response.data.link);
        }
      } else {
        toast({
          title: 'Oops!',
          description: 'Revise as informações do formulário e tente novamente.',
          status: 'error',
          isClosable: true,
        });
      }
      setIsLoading(false);
    } catch (err: any) {
      setIsLoading(false);
      let errorMessage = 'Revise as informações do formulário e tente novamente.';

      if (err?.response?.status === 500) {
        errorMessage = 'Erro interno no servidor. Por favor, tente novamente mais tarde.';
      } else if (err?.response?.data) {
        const errorData = err?.response?.data;
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (Object.keys(errorData).length) {
          errorMessage = Object.keys(errorData)
            .map((key) => `${errorData[key].join(' ')}`)
            .join(', ');
        }
      }

      toast({
        title: 'Oops!',
        description: errorMessage,
        status: 'error',
      });
    }
  }

  React.useEffect(() => {
    if (isOpen) {
      setValue('email', user?.email || '');
      setValue('password', '');
      setWorked(false);
    }
  }, [user, isOpen]);

  return (
    <Modal closeOnOverlayClick={false} closeOnEsc={false} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {worked ? 'Cadastro realizado com sucesso!' : 'Crie sua senha para acesso do sistema PDV'}
        </ModalHeader>
        <ModalCloseButton />
        {worked && (
          <ModalBody>
            <VStack spacing={4}>
              <Text fontSize={'12px'} color="#747474">
                Parabéns, cadastro criado com sucesso. Seu login e senha foram enviados por e-mail. Em breve um
                consultor entrará em contato para realizar um treinamento com você.
              </Text>
              <Text fontSize={'12px'} color="#747474">
                Para acessar entre no site:
                <Link href={link} rel="noreferrer" target="_blank" color={colors.primaryColor}>
                  {' '}
                  https://pdv.partiu.com.br
                </Link>
              </Text>
            </VStack>
          </ModalBody>
        )}
        {!worked && (
          <ModalBody>
            <VStack spacing={4}>
              <Input control={control} name="email" label="Email que será utilizado no login!" type="email" disabled />

              <Input label="Senha" control={control} type={'password'} name="password" />
              <Text fontSize={'12px'} color="#747474">
                A senha precisa de pelo menos 8 caracteres, precisando ter no minimo um numero e um caractere especial
                não pode ser parecido com seu email, não pode usar sequências como: 123, abc, etc..."
              </Text>
            </VStack>
          </ModalBody>
        )}
        <ModalFooter>
          <Button onClick={handleSubmit(onSubmit)} isLoading={isLoading} colorScheme="green">
            {worked ? 'Fechar' : 'Continuar'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
